import React, { useState, useEffect, useRef } from "react";
import {
  Handle,
  Position,
  NodeProps,
  useReactFlow,
  useEdges,
  useNodes,
} from "reactflow";
import { RotateCcw } from "lucide-react";
import { Input } from "@components/ui/input";
import { Button } from "@components/ui/button";
import { toast } from "@components/ui/use-toast";
import { executeWorkflow } from "../createPluginApis";
import { getExecutionPath, getConnectedInputs } from "../common/nodeDataFlow";
import { unwrapJsonData } from "../common/commonData";
import {
  getNodeExecutionParameters,
  buildWorkflowPayload,
  handleNodeExecutionResults,
  findDownstreamNodes,
} from "../utils/nodeExecutionUtils";
import { getNodeBackendType } from "../utils/nodeTransformUtils";
import PaginatedInputViewer from "../common/PaginatedInputViewer";
import ResponseViewer from "../common/ResponseViewer";
import NodeContentSection from "../common/NodeContentSection";
import { handlerStyle } from "../common/commonData";
import CollapsedNodeView from "../common/NodeHeaderView";
import NodeContextMenu, {
  useNodeContextMenu,
  createContextMenuItems,
} from "../common/NodeContextMenu";
import Confirmation from "@components/common/ConfirmDialog";
import { FullscreenWrapper } from "../common/withFullscreen";
import { FullscreenToggleButton } from "../common/FullscreenToggleButton";
import { ValidatedTitleInput } from "../components/ValidatedTitleInput";
import { useResizablePanels } from "../hooks/useResizablePanels";
import { PanelDivider } from "../common/PanelDivider";

interface LoopOverItemsNodeData {
  label?: string;
  header?: string;
  uid: string;
  title: string;
  description?: string;
  batchSize?: number;
  onDelete?: (id: string) => void;
  onDataChange?: (id: string, updates: any) => void;
  borderColor?: string;
  shadowColor?: string;
  iconStyle?: any;
  storedParameters?: any;
}

const LoopOverItemsNode: React.FC<NodeProps<LoopOverItemsNodeData>> = ({
  id,
  data,
}) => {
  const label = data.label || data.header || "Loop Over Items";
  const [uid, setUid] = useState(data.uid || "");
  const [title, setTitle] = useState(data.title || "");
  const [description, setDescription] = useState(data.description || "");
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [batchSize, setBatchSize] = useState<number | "">(data.batchSize || 1);
  const [reset, setReset] = useState<boolean>(false);
  const [isTitleValid, setIsTitleValid] = useState(true);
  // Input/Output state
  const [inputData, setInputData] = useState<any[]>([]);
  const [outputData, setOutputData] = useState<any>(null);
  const [errorResponse, setErrorResponse] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const reactFlowInstance = useReactFlow();
  const { containerRef, widths, startDrag } = useResizablePanels([
    33.33, 33.33, 33.34,
  ]);
  const { getNodes, getEdges } = useReactFlow();
  const prevOutputDataRef = useRef<any>(null);

  // Get edges and nodes using hooks for proper reactivity
  const edges = useEdges();
  const nodes = useNodes();

  // Context menu
  const {
    showContextMenu,
    contextMenuPosition,
    handleContextMenu,
    closeContextMenu,
  } = useNodeContextMenu(id);

  // Render input data section
  const renderInputData = () => {
    if (!inputData?.length) {
      return (
        <div className="text-xs text-muted-foreground p-4 border rounded-md bg-muted/20 text-center">
          <div className="font-medium">No Input Connected</div>
          <div>Connect a node to see data here</div>
        </div>
      );
    }

    // Build sources array for PaginatedInputViewer (handles tabs internally)
    const sources = inputData
      .filter((input) => input.nodeType !== "addButtonNode")
      .map((input, index) => {
        const displayData =
          input.response || input.outputData || input.configData || input;
        return {
          label: input.label || `Item ${index + 1}`,
          data: displayData,
          isConfig: !!input.configData,
        };
      });

    if (!sources.length) {
      return (
        <div className="text-xs text-muted-foreground p-4 border rounded-md bg-muted/20 text-center">
          <div className="font-medium">No Input Connected</div>
          <div>Connect a node to see data here</div>
        </div>
      );
    }
    return (
      <PaginatedInputViewer
        sources={sources}
        onRefresh={() => {
          const inputs = getConnectedInputs(id, edges, nodes);
          setInputData(inputs);
        }}
        sourceCount={inputData.length}
        height="100%"
      />
    );
  };

  // Auto-generate UID if not present
  useEffect(() => {
    if (!uid) {
      const newUid = `loop_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      setUid(newUid);
    }
  }, []);

  // Get input data from connected nodes - use stringified comparison to avoid infinite loops
  useEffect(() => {
    const inputs = getConnectedInputs(id, edges, nodes);
    setInputData(inputs);
  }, [
    id,
    JSON.stringify(edges.filter((e) => e.target === id || e.source === id)),
    JSON.stringify(
      nodes.map((n) => ({
        id: n.id,
        configData: (n.data as any)?.configData,
        response: (n.data as any)?.response,
        outputData: (n.data as any)?.outputData,
      })),
    ),
  ]);

  // Sync inputData from data.inputData prop (set during execution)
  useEffect(() => {
    const propInputData = (data as any).inputData;

    if (!propInputData || !Array.isArray(propInputData)) {
      return;
    }

    // Compare stringified data to avoid unnecessary updates
    const newDataStr = JSON.stringify(propInputData);
    const currentDataStr = JSON.stringify(inputData);

    if (newDataStr !== currentDataStr) {
      setInputData(propInputData);
    }
  }, [JSON.stringify((data as any).inputData)]);

  // Sync outputData from node data
  useEffect(() => {
    const newOutputData = (data as any).outputData;

    if (!newOutputData) {
      if (outputData !== null && newOutputData === null) {
        setOutputData(null);
      }
      return;
    }

    if (prevOutputDataRef.current === newOutputData) {
      return;
    }

    const newDataStr = JSON.stringify(newOutputData);
    const prevDataStr = prevOutputDataRef.current
      ? JSON.stringify(prevOutputDataRef.current)
      : null;

    if (newDataStr !== prevDataStr) {
      prevOutputDataRef.current = newOutputData;
      setOutputData(newOutputData);
    } else {
      prevOutputDataRef.current = newOutputData;
    }
  }, [(data as any).outputData]);

  // Sync error from node data (set during global workflow execution)
  useEffect(() => {
    const executionStatus = (data as any).executionStatus;
    const error = (data as any).error;

    // If node failed during global execution, show error in ResponseViewer
    if (executionStatus === "failed" && error) {
      setErrorResponse(error);
    } else if (executionStatus === "success") {
      // Clear error when node succeeds
      setErrorResponse(null);
    }
  }, [(data as any).executionStatus, (data as any).error]);

  // Sync data changes with parent
  useEffect(() => {
    // Don't save if batchSize is empty (user is actively editing)
    if (batchSize === "") return;

    const storedParameters: any = {
      batchSize: typeof batchSize === "number" ? batchSize : 1,
      options: {
        reset: reset,
      },
    };

    data.onDataChange?.(id, {
      uid,
      title,
      description,
      batchSize: typeof batchSize === "number" ? batchSize : 1,
      reset,
      outputData,
      sub_type: "loop_over_items",
      type: "flow",
      storedParameters,
    });
  }, [
    uid,
    title,
    description,
    batchSize,
    reset,
    outputData,
    id,
    data.onDataChange,
  ]);

  // Load saved parameters from data prop
  useEffect(() => {
    const params = (data as any).storedParameters || {};

    if (params.batchSize !== undefined) setBatchSize(params.batchSize);
    if (params.options?.reset !== undefined) setReset(params.options.reset);
  }, [data.storedParameters]);

  // Delete handler
  const handleDelete = (nodeId: string) => {
    setDeleteId(nodeId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (deleteId && data.onDelete) {
      data.onDelete(deleteId);
    }
    setShowDeleteConfirm(false);
    setDeleteId(null);
  };

  // Execute loop node
  const executeLoopNode = async () => {
    if (!title.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Title is required for the loop node",
        duration: 3000,
      });
      return;
    }

    // Get plugin ID from session
    const pluginId = sessionStorage.getItem("editPluginId");

    if (!pluginId || pluginId === "temp-plugin") {
      toast({
        variant: "destructive",
        title: "Plugin Not Saved",
        description: "Please save the plugin first before testing",
        duration: 4000,
      });
      return;
    }

    // Get active mesh flow from sessionStorage
    const activeMeshFlowStr = sessionStorage.getItem("activeMeshFlow");
    if (!activeMeshFlowStr) {
      toast({
        variant: "destructive",
        title: "Error",
        description:
          "No active mesh flow. Please create or select a mesh flow first.",
        duration: 4000,
      });
      return;
    }

    setLoading(true);
    setOutputData(null);
    setErrorResponse(null);

    try {
      const activeMeshFlow = JSON.parse(activeMeshFlowStr);

      // Get all nodes and edges from React Flow
      const allNodes = getNodes();
      const allEdges = getEdges();

      // Get execution path: all nodes from start to current node in topological order
      const executableNodes = getExecutionPath(id, allNodes, allEdges);

      // Mark all executable nodes as pending
      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          // Check if this node is in the execution path
          const isExecutable = executableNodes.some(
            (n: any) => n.id === node.id,
          );
          if (isExecutable) {
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "pending",
                executionResult: undefined,
                error: undefined,
              },
            };
          }
          return node;
        }),
      );

      // Also collect nodes from the loop chain (nodes connected from loop's "loop" handle)
      const loopChainNodes: any[] = [];
      const loopEdges = allEdges.filter(
        (e: any) => e.source === id && e.sourceHandle === "loop",
      );

      if (loopEdges.length > 0) {
        // Follow the chain to collect all nodes in the loop (excluding AddButton and back-to-loop edges)
        const visited = new Set<string>();
        const collectLoopNodes = (nodeId: string) => {
          if (visited.has(nodeId) || nodeId === id) return;
          visited.add(nodeId);

          const node = allNodes.find((n: any) => n.id === nodeId);
          if (
            node &&
            node.type !== "addButtonNode" &&
            !executableNodes.some((n: any) => n.id === nodeId)
          ) {
            loopChainNodes.push(node);
          }

          // Follow outgoing edges (excluding back to loop and AddButton)
          const outEdges = allEdges.filter((e: any) => {
            if (e.source !== nodeId) return false;
            const target = allNodes.find((n: any) => n.id === e.target);
            if (!target || target.type === "addButtonNode") return false;
            if (e.target === id) return false;
            return true;
          });

          outEdges.forEach((edge: any) => collectLoopNodes(edge.target));
        };

        loopEdges.forEach((edge: any) => collectLoopNodes(edge.target));
      }

      // Combine execution path nodes with loop chain nodes
      const allExecutableNodes = [...executableNodes, ...loopChainNodes];

      // Execute workflow ONCE with all nodes up to and including this node
      // Build nodes array - send all nodes in execution path (including config nodes)
      const workflowNodes = allExecutableNodes.map((node: any) => {
        // Determine backend type and sub_type using centralized mapping
        const backendTypeInfo = getNodeBackendType(node.type || "custom");
        let nodeType = backendTypeInfo.type;
        let subType = backendTypeInfo.sub_type;

        // Override type/subtype if explicitly set in node data
        if (node.data?.sub_type) {
          subType = node.data.sub_type;
          nodeType = node.data.type || "flow";
        }

        // Use centralized utility to get parameters (checks storedParameters first)
        const { parameters } = getNodeExecutionParameters(node, pluginId);

        return {
          uid: node.data.uid || node.id,
          title: node.data.title || node.data.header || "Untitled",
          description: node.data.description || "",
          type: nodeType,
          sub_type: subType,
          version: "1.0",
          position: [node.position.x, node.position.y],
          parameters,
        };
      });

      // Build connections between nodes - use all nodes in execution path
      const nodeIdToUidMap: Record<string, string> = {};
      allExecutableNodes.forEach((node: any) => {
        nodeIdToUidMap[node.id] = node.data.uid || node.id;
      });

      // Create a Set of node IDs in execution path for quick lookup
      const executionPathNodeIds = new Set(allExecutableNodes.map((n) => n.id));

      const connections: any = {};

      // Build connections from all edges in execution path
      allEdges.forEach((edge) => {
        // Special case: If edge is from AddButton to LoopNode, find the real source node
        // AddButton -> LoopNode should become LastNodeInChain -> LoopNode
        if (
          !executionPathNodeIds.has(edge.source) ||
          !executionPathNodeIds.has(edge.target)
        ) {
          // Check if target is a loop node (target must be executable)
          if (edge.target && executionPathNodeIds.has(edge.target)) {
            const targetNode = allExecutableNodes.find(
              (n: any) => n.id === edge.target,
            );
            if (
              targetNode &&
              (targetNode.type === "loopOverItemsNode" ||
                targetNode.data?.sub_type === "loop_over_items")
            ) {
              // This might be AddButton -> LoopNode
              // Find the incoming edge to the non-executable source (AddButton)
              const incomingEdge = allEdges.find(
                (e) =>
                  e.target === edge.source &&
                  executionPathNodeIds.has(e.source),
              );
              if (incomingEdge) {
                // Create connection from the real source node to the loop node
                const realSourceUid = nodeIdToUidMap[incomingEdge.source];
                const targetUid = nodeIdToUidMap[edge.target];

                if (realSourceUid && targetUid) {
                  if (!connections[realSourceUid]) {
                    connections[realSourceUid] = { main: [] };
                  }

                  if (!connections[realSourceUid].main[0]) {
                    connections[realSourceUid].main[0] = [];
                  }

                  // Add the connection (make sure it doesn't already exist)
                  const alreadyExists = connections[realSourceUid].main[0].some(
                    (conn: any) => conn.node === targetUid,
                  );

                  if (!alreadyExists) {
                    // Loop-back connection always uses index 1 (loop input)
                    connections[realSourceUid].main[0].push({
                      node: targetUid,
                      type: "main",
                      index: 1,
                    });
                  }
                }
              }
            }
          }
          return; // Skip this edge
        }
        const sourceUid = nodeIdToUidMap[edge.source];
        const targetUid = nodeIdToUidMap[edge.target];

        if (!sourceUid || !targetUid) return;

        if (!connections[sourceUid]) {
          connections[sourceUid] = {
            main: [],
          };
        }

        // Determine output index based on sourceHandle for loop nodes
        let outputIndex = 0;

        // Check if source is a loop node
        const sourceNode = allExecutableNodes.find(
          (n: any) => n.id === edge.source,
        );
        const isLoopNode =
          sourceNode?.type === "loopOverItemsNode" ||
          sourceNode?.data?.sub_type === "loop_over_items";

        if (isLoopNode && edge.sourceHandle) {
          // Loop nodes have two output handles: "done" (main[0]) and "loop" (main[1])
          outputIndex = edge.sourceHandle === "loop" ? 1 : 0;
        }

        if (!connections[sourceUid].main[outputIndex]) {
          connections[sourceUid].main[outputIndex] = [];
        }

        connections[sourceUid].main[outputIndex].push({
          node: targetUid,
          type: "main",
          index: 0,
        });
      });

      // Special handling for loop nodes: ensure both output arrays exist
      allExecutableNodes.forEach((node: any) => {
        const nodeUid = node.data.uid || node.id;
        const isLoopNode =
          node.type === "loopOverItemsNode" ||
          node.data?.sub_type === "loop_over_items";

        if (isLoopNode) {
          // Initialize connection arrays if they don't exist
          if (!connections[nodeUid]) {
            connections[nodeUid] = { main: [[], []] };
          }

          // Ensure both main[0] (done) and main[1] (loop) arrays exist
          if (!connections[nodeUid].main[0]) {
            connections[nodeUid].main[0] = [];
          }
          if (!connections[nodeUid].main[1]) {
            connections[nodeUid].main[1] = [];
          }

          // Don't auto-add self-loop - it's created by the actual edges
          // The loop is formed by: LoopNode -> HttpNode -> LoopNode
          // This is handled by the edge transformation above
        }
      });

      // Build workflow payload for this execution (all nodes up to current)
      const workflowPayload = buildWorkflowPayload(
        activeMeshFlow,
        workflowNodes,
        connections,
      );

      const result = await executeWorkflow(workflowPayload);

      // Update ALL executed nodes with their results (not just the clicked node)
      if (result?.results?.[0]?.node_results) {
        const nodeResults = result.results[0].node_results;
        const allEdges = reactFlowInstance.getEdges();

        // Use the utility function to handle node execution results
        const { hasErrors, errorMessage, errorNodeTitle } =
          handleNodeExecutionResults(reactFlowInstance, nodeResults, allEdges);

        // Extract this node's output for local state
        const resultsMap = new Map<string, any>();
        nodeResults.forEach((nodeResult: any) => {
          resultsMap.set(nodeResult.uid, nodeResult);
        });

        // Update the current node's output data
        const currentNodeResult = resultsMap.get(uid || id);
        if (currentNodeResult) {
          // Check if this node failed
          const isFailed =
            currentNodeResult.status === "error" ||
            currentNodeResult.status === "failed" ||
            currentNodeResult.status === "failure";

          if (isFailed) {
            // Show error in ResponseViewer but don't pass to downstream nodes
            setErrorResponse(currentNodeResult.error || "Execution failed");
            setOutputData(null);
          } else if (currentNodeResult.output_data) {
            // Success - extract and display output data
            const unwrappedResponse = unwrapJsonData(
              currentNodeResult.output_data,
            );
            setOutputData(unwrappedResponse);
            setErrorResponse(null);
          }
        }

        // Show appropriate toast based on execution result
        if (hasErrors) {
          const errorDescription = errorMessage
            ? `${errorNodeTitle}: ${errorMessage}`
            : "One or more nodes failed during execution";

          const shortError =
            errorDescription.length > 150
              ? errorDescription.substring(0, 150) + "..."
              : errorDescription;

          toast({
            variant: "destructive",
            title: "Execution Failed",
            description: shortError,
            duration: 7000,
          });
        } else {
          // Only show success if ALL nodes succeeded
          toast({
            variant: "success",
            title: "Success",
            description: "Loop Over Items executed successfully",
            duration: 3000,
          });
        }
      } else {
        // Fallback - no node results in response
        const isSuccess =
          result?.success === true ||
          result?.status === "success" ||
          result?.body?.status === 1 ||
          result?.body?.status === "1" ||
          result?.status === 1;

        if (!isSuccess) {
          const allEdges = reactFlowInstance.getEdges();
          const downstreamNodes = findDownstreamNodes(id, allEdges);

          reactFlowInstance.setNodes((nodes) =>
            nodes.map((node) => {
              if (node.id === id) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "failed",
                    error:
                      result?.body?.message ||
                      result?.message ||
                      "Execution failed",
                  },
                };
              } else if (downstreamNodes.has(node.id)) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "failed",
                    error: "Not executed due to upstream failure",
                  },
                };
              }
              return node;
            }),
          );

          toast({
            variant: "destructive",
            title: "Execution Error",
            description:
              result?.body?.message || result?.message || "Execution failed",
            duration: 4000,
          });
        }
      }
    } catch (error: any) {
      console.error("Loop Over Items Execution Error:", error);
      setErrorResponse(error?.message || "Loop Over Items execution failed");
      setOutputData(null);

      const allEdges = reactFlowInstance.getEdges();
      const downstreamNodes = findDownstreamNodes(id, allEdges);

      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === id) {
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "failed",
                error: error?.message || "Node execution failed",
              },
            };
          } else if (downstreamNodes.has(node.id)) {
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "failed",
                error: "Not executed due to upstream failure",
              },
            };
          }
          return node;
        }),
      );

      toast({
        variant: "destructive",
        title: "Error",
        description: error?.message || "Node execution failed",
        duration: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <FullscreenWrapper
      id={id}
      isExpanded={isExpanded}
      executionStatus={(data as any).executionStatus}
      borderColor={data.borderColor}
      shadowColor={data.shadowColor}
      onContextMenu={handleContextMenu}
      expandedClassName="w-[90vw] !max-w-[1000px] flex flex-col"
    >
      <Confirmation
        text="Are you sure you want to delete this node?"
        confirmAction={confirmDelete}
        rejectAction={() => setShowDeleteConfirm(false)}
        confirmText="Confirm"
        rejectText="Cancel"
        show={showDeleteConfirm}
        closeDialog={() => setShowDeleteConfirm(false)}
      />

      <NodeContextMenu
        show={showContextMenu}
        position={contextMenuPosition}
        onClose={closeContextMenu}
        items={[
          createContextMenuItems.expandCollapse(isExpanded, () =>
            setIsExpanded((prev) => !prev),
          ),
          createContextMenuItems.delete(
            () => handleDelete(id),
            !!data.onDelete,
          ),
        ]}
      />

      <Handle
        id="input"
        type="target"
        position={Position.Left}
        className={handlerStyle}
        style={{ left: -8, zIndex: 10 }}
      />
      <Handle
        id="done"
        type="source"
        position={Position.Right}
        className={handlerStyle}
        style={{ top: "30%", right: -8, zIndex: 10 }}
      />
      {!isExpanded && (
        <div
          className="absolute bg-background text-primaryGreen rounded-xl px-1 text-[8px] font-semibold text-muted-foreground whitespace-nowrap pointer-events-none"
          style={{
            top: "30%",
            transform: "translateY(-50%)",
            left: "calc(100% + 8px)",
          }}
        >
          Done
        </div>
      )}
      <Handle
        id="loop"
        type="source"
        position={Position.Right}
        className={handlerStyle}
        style={{ top: "70%", right: -8, zIndex: 10 }}
      />
      {!isExpanded && (
        <div
          className="absolute bg-background text-primaryGreen rounded-xl px-1 text-[8px] font-semibold text-muted-foreground whitespace-nowrap pointer-events-none"
          style={{
            top: "70%",
            transform: "translateY(-50%)",
            left: "calc(100% + 8px)",
          }}
        >
          Loop
        </div>
      )}

      {/* Header — flex-shrink-0 */}
      <div
        className={`flex items-center justify-between gap-4 rounded-md transition-all duration-500 flex-shrink-0 ${
          isExpanded ? "p-3" : "!p-3"
        }`}
      >
        <CollapsedNodeView
          icon={
            <RotateCcw
              className={
                (data as any).executionStatus === "running" ||
                (data as any).executionStatus === "pending"
                  ? "animate-spin"
                  : ""
              }
              style={
                (data as any).executionStatus === "running" ||
                (data as any).executionStatus === "pending"
                  ? { animationDirection: "reverse", animationDuration: "1s" }
                  : {}
              }
            />
          }
          label={label}
          title={title}
          iconSize={35}
          borderColor={data.borderColor}
          onToggle={() => setIsExpanded((prev) => !prev)}
          isExpanded={isExpanded}
          executionStatus={(data as any).executionStatus}
          description={description}
        />
        <FullscreenToggleButton isExpanded={isExpanded} />
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {/* Metadata Form — flex-shrink-0 */}
          <div className="mt-2 p-4 border-t flex items-start gap-4 flex-shrink-0">
            <div className="space-y-1 w-1/2">
              <label className="text-xs font-medium text-muted-foreground">
                Node Title <span className="text-primary">*</span>
              </label>
              <ValidatedTitleInput
                id={id}
                title={title}
                onChange={setTitle}
                nodes={nodes}
                dataTitle={data.title}
                onValidationChange={setIsTitleValid}
              />
            </div>
            <div className="space-y-1 w-1/2">
              <label className="text-xs font-medium text-muted-foreground">
                Node Description (optional)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter node description"
                className="w-full p-2 border rounded text-xs resize-none text-muted-foreground bg-background"
                rows={1}
              />
            </div>
          </div>

          {/* 3 Section Grid — flex-1 min-h-0 fills remaining height */}
          <div
            ref={containerRef}
            className="flex gap-0 p-4 border-t flex-1 min-h-0 overflow-hidden"
          >
            {/* Left — Input Data */}
            <div
              style={{ width: `${widths[0]}%`, minWidth: 80, flexShrink: 0 }}
            >
              <NodeContentSection scrollable={false} hasRelative={true}>
                {renderInputData()}
              </NodeContentSection>
            </div>

            <PanelDivider onMouseDown={startDrag(0)} />

            {/* Middle — Parameters */}
            <div
              style={{ width: `${widths[1]}%`, minWidth: 80, flexShrink: 0 }}
            >
              <NodeContentSection hasRelative={true} scrollable={false}>
                {/* Scroll wrapper */}
                <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden flex flex-col gap-2">
                  <h3 className="text-sm font-semibold sticky top-0 bg-background z-10 pb-2 border-b flex-shrink-0">
                    Parameters
                  </h3>

                  <div className="space-y-3 mt-4">
                    <div className="space-y-1">
                      <label className="text-xs">Batch Size</label>
                      <Input
                        type="number"
                        min={1}
                        value={batchSize}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "") {
                            setBatchSize("");
                          } else {
                            const num = parseInt(val);
                            if (!isNaN(num)) setBatchSize(num);
                          }
                        }}
                        placeholder="Enter batch size"
                        className="text-xs h-8"
                      />
                      <p className="text-[10px] text-muted-foreground">
                        Number of items to process in each loop iteration
                      </p>
                    </div>

                    <div className="mt-4 p-3 bg-muted/20 rounded border space-y-2">
                      <p className="text-xs font-semibold mb-2">
                        Loop Behavior
                      </p>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="reset-loop"
                          checked={reset}
                          onChange={(e) => setReset(e.target.checked)}
                          className="w-3 h-3"
                        />
                        <label htmlFor="reset-loop" className="text-xs">
                          Reset on each iteration
                        </label>
                      </div>
                      <p className="text-[10px] text-muted-foreground">
                        When enabled, resets loop state after each batch
                      </p>
                    </div>
                  </div>
                </div>
                {/* End scroll wrapper */}

                {/* Execute button — outside scroll, pinned at bottom */}
                <div className="flex-shrink-0 pt-2 border-t bg-background">
                  <Button
                    onClick={executeLoopNode}
                    disabled={
                      !title.trim() ||
                      loading ||
                      batchSize === "" ||
                      batchSize < 1 ||
                      !isTitleValid
                    }
                    className="w-full rounded-md"
                    size="sm"
                  >
                    {loading ? (
                      "Executing..."
                    ) : (
                      <>
                        <RotateCcw size={14} className="mr-1" /> Execute Loop
                      </>
                    )}
                  </Button>
                </div>
              </NodeContentSection>
            </div>

            <PanelDivider onMouseDown={startDrag(1)} />

            {/* Right — Output Data */}
            <div
              style={{ width: `${widths[2]}%`, minWidth: 80, flexShrink: 0 }}
            >
              <NodeContentSection scrollable={false} hasRelative={true}>
                <ResponseViewer
                  response={errorResponse || outputData}
                  outputLabels={{
                    primary: "Done",
                    secondary: "Loop",
                  }}
                  isError={!!errorResponse}
                  isLoopChild={(data as any).is_loop_child || false}
                  totalIterations={(data as any).total_iterations || 0}
                  iterations={(data as any).iterations || []}
                  fillParent={true}
                  onResponseChange={(newData) => {
                    setOutputData(newData);
                    data.onDataChange?.(id, {
                      response: newData,
                      manuallyEdited: true,
                    });
                  }}
                />
              </NodeContentSection>
            </div>
          </div>
        </div>
      )}
    </FullscreenWrapper>
  );
};

export default LoopOverItemsNode;
